import React from 'react'
import PropTypes from 'prop-types'
import { isPlainObject, omit, pick } from 'lodash'
import Component from './Component'
import SelectField from './SelectField'
import JsonField from './JsonField'

// PD-30655: Form.js is flat (newState[field] = value) and overwrites every
// child's handleChange via cloneElement, so no child rendered directly under
// Form can own just a slice of `args` -- one component has to own the whole
// `args` value and compose it. ArgsField is that component: it renders one
// SelectField per template key resolved to a picker ({label:, options:}),
// and a single JsonField carrying every remaining (free-text) key, merging
// both halves into one `args` object before ever calling handleChange.
export default class ArgsField extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.object,
    handleChange: PropTypes.func,
    template: PropTypes.object,
    errorMessage: PropTypes.string
  }

  static defaultProps = {
    value: {},
    handleChange: () => {},
    template: {}
  }

  get pickerKeys() {
    return Object.keys(this.props.template).filter((key) => isPlainObject(this.props.template[key]))
  }

  get textKeys() {
    const pickerKeys = this.pickerKeys
    return Object.keys(this.props.template).filter((key) => !pickerKeys.includes(key))
  }

  // Render the textarea when the tool declares a free-text arg, OR when the
  // value carries anything that is not a picker.
  //
  // The second half is not redundant. Both SBC tools declare `version` and
  // nothing else, so a picker-only template has no text keys at all -- and
  // asking only the template would hide an undeclared key completely, with
  // no control anywhere on the form able to show or remove it. `args` is a
  // free-form JSONB column, so such a key is possible however it got there.
  get hasTextKeys() {
    return this.textKeys.length > 0 || Object.keys(this.remainder).length > 0
  }

  get pickers() {
    return this.pickerKeys.map((key) => {
      const { label, options } = this.props.template[key]
      return (
        <SelectField
          key={key}
          name={key}
          label={label}
          options={options}
          value={this.props.value[key]}
          handleChange={this.handlePickerChange}
        />
      )
    })
  }

  // Everything that is not a picker, rather than only the free-text keys the
  // template happens to declare. An operator can type a key the template
  // never mentioned, and `args` is a free-form JSONB column that accepts it.
  //
  // Selecting by template key instead would hide such a key on the very next
  // render while the parent still held it: invisible in the textarea, still
  // in the payload, and then dropped the next time the textarea is edited.
  // The operator can neither see it nor remove it deliberately.
  get remainder() {
    return omit(this.props.value, this.pickerKeys)
  }

  // A picker owns exactly one key, so its change merges onto everything
  // else and the whole object goes out -- the submitted payload stays
  // identical to typing the lot by hand.
  handlePickerChange = (key, value) => {
    this.props.handleChange(this.props.name, { ...this.props.value, [key]: value })
  }

  // The textarea is AUTHORITATIVE over every key it shows, so its content
  // REPLACES the free-text half rather than merging onto it. Merging looks
  // equivalent and is not: a key the operator deletes gets silently put
  // back, and the tool then runs with an argument nobody can see they sent.
  //
  // On DeleteSheetRates that is destructive. Deleting the `hios_ids` line
  // today fails loudly with "Missing required arguments: hios_ids"; merged,
  // the `["HIOS ID"]` placeholder returns, the query filters on a HIOS id
  // that matches nothing, and the run reports "Completed - 0 Deleted". A
  // refusal becomes a silent no-op.
  //
  // Only the picker keys are carried across, because those are the keys
  // this textarea never showed.
  handleRemainderChange = (name, remainder) => {
    this.props.handleChange(this.props.name, { ...pick(this.props.value, this.pickerKeys), ...remainder })
  }

  render() {
    return (
      <div>
        {this.pickers}
        {this.hasTextKeys && (
          <JsonField
            key="remainder"
            name="remainder"
            label="Arguments"
            rows="10"
            value={this.remainder}
            handleChange={this.handleRemainderChange}
            errorMessage={this.props.errorMessage}
          />
        )}
      </div>
    )
  }
}
